import { Box, Typography } from '@mui/material';
import Header from '@/Components/MagicLink/Header';
import ParameterItem from '@/Components/MagicLink/ParameterItem';
import PrimaryActionButton from '@/Components/MagicLink/PrimaryActionButton';
import IdCardBadge from '@/Components/MagicLink/IdCardBadge';
import Section from '@/Components/MagicLink/Section';
import MagicLinkActionBar from '@/Components/MagicLink/MagicLinkActionBar';
import BackActionButton from '@/Components/MagicLink/BackActionButton';
import { useMagicLinkAppProvider, MagicLinkView, getToken } from '@/Provider/MagicLinkAppProvider';
import { magicLinkPageColumnSx, magicLinkPageOuterSx } from '@/Utils/MagicLink/pageLayout';

const RequestDetailView = () => {
    const { requestDetail, navigateToView, magicLinkData } = useMagicLinkAppProvider();

    const linkTokenHash = getToken();

    const idCardFrontFile = magicLinkData?.requestFiles?.find(file => file.taxonomyId === 41);
    const idCardBackFile = magicLinkData?.requestFiles?.find(file => file.taxonomyId === 42);

    const getImageUrl = (requestFileId: string | null | undefined): string | null => {
        if (!linkTokenHash || !requestFileId) return null;
        return `/api/magic-link/public/streamIdCardSideFile?linkTokenHash=${encodeURIComponent(linkTokenHash)}&requestFileId=${requestFileId}`;
    };

    const groupedParams = (requestDetail?.params || []).reduce((groups, param) => {
        const sectionKey = param.section || '';
        const existing = groups.find((group) => group.section === sectionKey);
        if (existing) {
            existing.items.push(param);
        } else {
            groups.push({ section: sectionKey, items: [param] });
        }
        return groups;
    }, [] as { section: string; items: { section: string | null; label: string; value: string }[] }[]);

    const hasIdCardImages =
        (magicLinkData?.idCardFrontUploaded && idCardFrontFile && !!getImageUrl(idCardFrontFile.id)) ||
        (magicLinkData?.idCardBackUploaded && idCardBackFile && !!getImageUrl(idCardBackFile.id));

    return (
        <Box component="main" sx={magicLinkPageOuterSx}>
            <Box sx={magicLinkPageColumnSx}>
                    <Header />

                    <Typography variant="h6" component="h1" sx={{ fontWeight: 600, width: '100%' }}>
                        {requestDetail?.headline || 'Request Detail'}
                    </Typography>

                    {groupedParams.map((group, groupIndex) => (
                        <Section
                            key={`${group.section}-${groupIndex}`}
                            title={group.section ? group.section : undefined}
                            disableBodyPadding
                            disableBodyGap
                        >
                            {group.items.map((item, itemIndex) => (
                                <ParameterItem
                                    key={`${group.section}-${item.label}-${itemIndex}`}
                                    label={item.label}
                                    value={item.value}
                                    isLast={itemIndex === group.items.length - 1}
                                />
                            ))}
                        </Section>
                    ))}

                    {hasIdCardImages && (
                        <Section title="Občiansky preukaz">
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    flexWrap: 'wrap',
                                    gap: 2,
                                }}
                            >
                                {magicLinkData?.idCardFrontUploaded && idCardFrontFile && getImageUrl(idCardFrontFile.id) && (
                                    <IdCardBadge
                                        label="Predná strana OP"
                                        imageUrl={getImageUrl(idCardFrontFile.id)}
                                        fileId={idCardFrontFile.id}
                                        fileName={idCardFrontFile.name}
                                        linkTokenHash={linkTokenHash}
                                    />
                                )}
                                {magicLinkData?.idCardBackUploaded && idCardBackFile && getImageUrl(idCardBackFile.id) && (
                                    <IdCardBadge
                                        label="Zadná strana OP"
                                        imageUrl={getImageUrl(idCardBackFile.id)}
                                        fileId={idCardBackFile.id}
                                        fileName={idCardBackFile.name}
                                        linkTokenHash={linkTokenHash}
                                    />
                                )}
                            </Box>
                        </Section>
                    )}

                    <MagicLinkActionBar
                        secondary={
                            <BackActionButton onClick={() => navigateToView(MagicLinkView.INTRO)} />
                        }
                        primary={<PrimaryActionButton fullWidth />}
                    />
            </Box>
        </Box>
    );
};

export default RequestDetailView;
